import { useMemo, useRef, useState } from 'react'
import { MAX_TAGS_PER_FILE, normalizeTag } from '../utils/docTypes'

interface TagInputProps {
  value: string[]
  onChange: (tags: string[]) => void
  /** Previously used tags in this project, most-used first (autocomplete source). */
  suggestions: { name: string; usageCount: number }[]
  disabled?: boolean
  placeholder?: string
}

/**
 * Tag chips input with autocomplete from previously used project tags.
 * New entries are allowed and normalized live (e.g. "Mobile Auth" →
 * "mobile-auth") so the user sees the stored form before committing.
 */
export default function TagInput({
  value,
  onChange,
  suggestions,
  disabled = false,
  placeholder = 'Add tags…',
}: TagInputProps) {
  const [draft, setDraft] = useState('')
  const [focused, setFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const normalizedDraft = normalizeTag(draft)

  const matchingSuggestions = useMemo(() => {
    const needle = (normalizedDraft ?? draft.trim().toLowerCase()).toLowerCase()
    return suggestions
      .filter((s) => !value.includes(s.name))
      .filter((s) => needle.length === 0 || s.name.includes(needle))
      .slice(0, 8)
  }, [suggestions, value, draft, normalizedDraft])

  const addTag = (raw: string) => {
    const normalized = normalizeTag(raw)
    if (!normalized) return
    if (value.includes(normalized)) {
      setDraft('')
      return
    }
    if (value.length >= MAX_TAGS_PER_FILE) return
    onChange([...value, normalized])
    setDraft('')
  }

  const removeTag = (tag: string) => {
    onChange(value.filter((t) => t !== tag))
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      addTag(draft)
    } else if (e.key === 'Backspace' && draft.length === 0 && value.length > 0) {
      removeTag(value[value.length - 1])
    }
  }

  const showDropdown = focused && !disabled && (matchingSuggestions.length > 0 || draft.length > 0)

  return (
    <div className="relative">
      <div
        className={`flex flex-wrap items-center gap-2 px-3 py-2 bg-harbor-surface-raised border border-harbor-border rounded-lg min-h-[42px] ${
          disabled ? 'opacity-50' : 'focus-within:ring-2 focus-within:ring-emerald-500'
        }`}
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-500/20 text-emerald-300 text-xs font-medium rounded"
          >
            {tag}
            {!disabled && (
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="text-emerald-400 hover:text-white"
                aria-label={`Remove tag ${tag}`}
              >
                ×
              </button>
            )}
          </span>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={draft}
          disabled={disabled || value.length >= MAX_TAGS_PER_FILE}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            // Delay so a suggestion click registers before the dropdown closes.
            setTimeout(() => setFocused(false), 150)
          }}
          placeholder={value.length === 0 ? placeholder : ''}
          className="flex-1 min-w-[100px] bg-transparent text-sm text-harbor-text placeholder-harbor-text-muted focus:outline-none"
        />
      </div>

      {draft.trim().length > 0 && normalizedDraft && normalizedDraft !== draft.trim() && (
        <p className="text-xs text-harbor-text-muted mt-1">
          Will be saved as <span className="text-emerald-400">{normalizedDraft}</span>
        </p>
      )}
      {draft.trim().length > 0 && !normalizedDraft && (
        <p className="text-xs text-amber-400 mt-1">
          Invalid tag — use letters, digits, and hyphens (max 32 chars)
        </p>
      )}
      {value.length >= MAX_TAGS_PER_FILE && (
        <p className="text-xs text-amber-400 mt-1">Maximum {MAX_TAGS_PER_FILE} tags per file</p>
      )}

      {showDropdown && matchingSuggestions.length > 0 && (
        <div className="absolute z-10 mt-1 w-full bg-harbor-surface-raised border border-harbor-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {matchingSuggestions.map((s) => (
            <button
              key={s.name}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault()
                addTag(s.name)
              }}
              className="w-full text-left px-3 py-2 text-sm text-harbor-text hover:bg-harbor-border flex items-center justify-between"
            >
              <span>{s.name}</span>
              <span className="text-xs text-harbor-text-muted">{s.usageCount} file(s)</span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
